import { useEffect, useRef, useCallback, useState } from 'react';
import {
  getChatAblyClient,
  getOrderChatChannel,
} from '@/utils/ably';
import type { ChatMessage } from '@/utils/ably';
import type { Realtime } from 'ably';

/**
 * Token 2 — Order Chat (Customer ↔ Staff/Admin)
 * Bidirectional chat on a channel scoped to orderId.
 */

export function useAblyChat(orderId: string | null) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const messagesRef = useRef<ChatMessage[]>([]);
  const clientRef = useRef<Realtime | null>(null);
  const [ready, setReady] = useState(false);

  // Sync ref with state
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Initialize chat Ably client async
  useEffect(() => {
    let cancelled = false;
    getChatAblyClient().then((c) => {
      if (cancelled) return;
      clientRef.current = c;
      setReady(!!c);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // ─── Subscribe to order chat ───────────────────────────────────────
  const subscribeToOrderChat = useCallback(() => {
    const client = clientRef.current;
    if (!client || !ready || !orderId) return;

    const channel = getOrderChatChannel(client, orderId);

    channel.on('attached', () => {
      if (import.meta.env.DEV) console.log(`[Ably-Chat] Attached to chat:order:${orderId}`);
      setIsConnected(true);
    });

    if (channel.state === 'attached') {
      setIsConnected(true);
    }

    channel.subscribe('chat_message', (msg) => {
      const payload = msg.data as ChatMessage;
      if (import.meta.env.DEV) console.log('[Ably-Chat] New message:', payload);

      setMessages((prev) => {
        // Dedupe by id
        const next = prev.find((m) => m.id === payload.id) ? prev : [...prev, payload];
        try {
          localStorage.setItem(`ably_chat_${orderId}`, JSON.stringify(next));
        } catch (e) {
          console.warn('Failed to cache chat', e);
        }
        return next;
      });
    });

    return () => {
      channel.unsubscribe();
      channel.detach();
    };
  }, [ready, orderId]);

  // ─── Send message ──────────────────────────────────────────────────
  const sendMessage = useCallback(
    (
      text: string,
      senderId: string,
      senderRole: 'customer' | 'staff' | 'admin' | 'manager',
      senderName: string
    ) => {
      const client = clientRef.current;
      if (!client || !ready || !orderId) return;

      // M14: Sanitize input — trim, enforce max length, reject empty
      const sanitized = text.trim().slice(0, 1000);
      if (!sanitized) return;

      const message: ChatMessage = {
        id: crypto.randomUUID(),
        senderId,
        senderRole,
        senderName,
        text: sanitized,
        timestamp: Date.now(),
      };

      const channel = getOrderChatChannel(client, orderId);
      channel.publish('chat_message', message);

      // Optimistically add to local state
      setMessages((prev) => {
        const next = [...prev, message];
        try {
          localStorage.setItem(`ably_chat_${orderId}`, JSON.stringify(next));
        } catch (e) {
          console.warn('Failed to cache chat', e);
        }
        return next;
      });
    },
    [ready, orderId]
  );

  // ─── Load history (placeholder — Ably history requires API key with capabilities) ─
  const loadHistory = useCallback(async () => {
    const client = clientRef.current;
    if (!client || !ready || !orderId) return;

    try {
      const channel = getOrderChatChannel(client, orderId);
      const history = await channel.history({ limit: 50 });
      const items = history.items
        .map((msg) => msg.data as ChatMessage)
        .filter(Boolean);
      setMessages(items);
    } catch (err) {
      if (import.meta.env.DEV) console.warn('[Ably-Chat] History load failed:', err);
    }
  }, [ready, orderId]);

  // ─── Auto-subscribe when orderId changes ───────────────────────────
  useEffect(() => {
    if (!orderId || !ready) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setMessages([]);
      return;
    }

    // Try loading from localStorage first for immediate display
    try {
      const cached = localStorage.getItem(`ably_chat_${orderId}`);
      if (cached) {
         
        setMessages(JSON.parse(cached));
      }
    } catch (e) {
      if (import.meta.env.DEV) console.warn('Failed to parse chat cache', e);
    }

    const unsubscribe = subscribeToOrderChat();
    void loadHistory();

    return () => {
      unsubscribe?.();
    };
  }, [orderId, ready, subscribeToOrderChat, loadHistory]);

  return {
    messages,
    isConnected,
    sendMessage,
    loadHistory,
  };
}
